import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { ArrowLeftRight, Route } from 'lucide-react'
import { Container } from '../components/ui'
import EntityPicker, { type PickedEntity } from '../components/connections/EntityPicker'
import ConnectionResults from '../components/connections/ConnectionResults'
import { findConnections, type ConnectionResult } from '../services/apiClient'
import { usePageTitle } from '../hooks/usePageTitle'

type Mode = 'all' | 'ownership'

function readEntity(params: URLSearchParams, key: 'fra' | 'til'): PickedEntity | null {
  const id = params.get(key)
  if (!id || !/^(person:\d{1,20}|cvr:\d{8})$/.test(id)) return null
  return { id, name: params.get(`${key}Navn`) || id }
}

/**
 * »Find forbindelse«: how is a person or company connected to another?
 * Current ownership and roles first — a direct link or the companies both
 * are in, strongest tie on top, then longer chains. Then a second search
 * through former roles and founders, for the links that have since been
 * cut: two people who were in the same company years apart.
 */

/** Paths from the history search that use at least one ended role and are not already shown. */
function formerOnly(past: ConnectionResult, now: ConnectionResult): ConnectionResult {
  const key = (p: ConnectionResult['paths'][number]) => p.nodes.map((n) => n.id).join('>')
  const seen = new Set(now.paths.map(key))
  return {
    truncated: past.truncated,
    paths: past.paths.filter((p) => !seen.has(key(p)) && p.edges.some((e) => e.current === false)),
  }
}

const FindConnectionPage: React.FC = () => {
  usePageTitle('Find forbindelse')
  const [params, setParams] = useSearchParams()
  const [from, setFrom] = useState<PickedEntity | null>(() => readEntity(params, 'fra'))
  const [to, setTo] = useState<PickedEntity | null>(() => readEntity(params, 'til'))
  const [mode, setMode] = useState<Mode>(params.get('mode') === 'ownership' ? 'ownership' : 'all')
  const [result, setResult] = useState<ConnectionResult | null>(null)
  const [past, setPast] = useState<ConnectionResult | null>(null)
  const [pastLoading, setPastLoading] = useState(false)
  const searchId = useRef(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (a: PickedEntity, b: PickedEntity, m: Mode) => {
    const id = ++searchId.current
    setLoading(true)
    setError(null)
    setResult(null)
    setPast(null)
    setPastLoading(false)
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('fra', a.id)
        next.set('fraNavn', a.name)
        next.set('til', b.id)
        next.set('tilNavn', b.name)
        if (m === 'ownership') next.set('mode', 'ownership')
        else next.delete('mode')
        return next
      },
      { replace: true }
    )
    let now: ConnectionResult
    try {
      now = await findConnections(a.id, b.id, m)
      if (id !== searchId.current) return
      setResult(now)
    } catch (err: unknown) {
      if (id === searchId.current) setError(err instanceof Error ? err.message : 'Fejl')
      return
    } finally {
      if (id === searchId.current) setLoading(false)
    }
    // Then the links that have since been cut.
    setPastLoading(true)
    try {
      const history = await findConnections(a.id, b.id, m, 5, true)
      if (id === searchId.current) setPast(formerOnly(history, now))
    } catch {
      // The current answer stands; former roles are a bonus.
    } finally {
      if (id === searchId.current) setPastLoading(false)
    }
  }

  // Shared link with both ends filled in: search straight away.
  useEffect(() => {
    if (from && to && params.get('auto') !== '0') void run(from, to, mode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const canSearch = !!from && !!to && from.id !== to.id && !loading

  return (
    <Container className="pt-7 pb-16">
      <div className="max-w-[880px]">
        <h1 className="text-[26px] sm:text-[34px] leading-[1.1] text-ink">Find forbindelse</h1>
        <p className="mt-2 text-[15px] text-ink-700 max-w-[62ch]">
          Hvordan hænger to personer eller selskaber sammen? Søgningen følger ejerskab og roller i
          ledelsen i CVR — først de nuværende, så tidligere roller og stiftere. Et led er ét skridt som
          »A ejer B«, og søgningen går højst 5 led ud.
        </p>

        <form
          className="mt-6 rounded-[10px] border border-rule bg-paper p-5"
          onSubmit={(e) => {
            e.preventDefault()
            if (from && to && canSearch) void run(from, to, mode)
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <EntityPicker label="Fra" value={from} onChange={setFrom} />
            <button
              type="button"
              onClick={() => {
                setFrom(to)
                setTo(from)
              }}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-lg border border-rule-strong bg-paper text-ink-600 hover:border-ink-300 sm:self-end"
              aria-label="Byt om på fra og til"
              title="Byt om"
            >
              <ArrowLeftRight className="h-4 w-4" aria-hidden />
            </button>
            <EntityPicker label="Til" value={to} onChange={setTo} />
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-full border border-rule-strong bg-paper p-0.5" role="group" aria-label="Følg">
              {(['all', 'ownership'] as Mode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                  className={`h-8 rounded-full px-3.5 text-[13px] font-semibold ${mode === m ? 'bg-ink text-paper' : 'text-ink-600 hover:text-ink'}`}
                >
                  {m === 'all' ? 'Ejerskab og ledelse' : 'Kun ejerskab'}
                </button>
              ))}
            </div>
            <button
              type="submit"
              disabled={!canSearch}
              className="inline-flex h-[38px] items-center gap-2 rounded-[7px] bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink-700 disabled:bg-ink-300"
            >
              <Route className="h-4 w-4" aria-hidden />
              {loading ? 'Søger…' : 'Find forbindelse'}
            </button>
          </div>
        </form>

        <section className="mt-6" aria-live="polite">
          {loading && (
            <p className="text-sm text-ink-500">
              Søger gennem ejerskab og ledelse… Store netværk kan tage op til et halvt minut.
            </p>
          )}
          {error && <p className="text-sm text-signal-700 bg-signal-50 rounded-md px-3 py-2">{error}</p>}
          {result && !loading && (
            <>
              {result.paths.length === 0 ? (
                <h2 className="text-[17px] font-bold text-ink">Ingen nuværende forbindelse inden for 5 led</h2>
              ) : (
                <ConnectionResults result={result} />
              )}
              {result.truncated && (
                <p className="mt-2 text-sm text-ink-500">
                  Søgningen blev begrænset, fordi netværket er stort. Der kan findes længere eller andre
                  forbindelser{mode === 'all' ? ' — prøv »Kun ejerskab« for en mere målrettet søgning' : ''}.
                </p>
              )}
              {pastLoading && (
                <p className={`text-sm text-ink-500 ${result.paths.length > 0 ? 'mt-8' : 'mt-2'}`}>
                  Leder også gennem tidligere roller og stiftere…
                </p>
              )}
              {past && past.paths.length > 0 && (
                <section className={result.paths.length > 0 ? 'mt-10' : 'mt-6'} aria-labelledby="tidligere-forbindelser">
                  <h2 id="tidligere-forbindelser" className="text-[17px] font-bold text-ink">
                    Tidligere forbindelser
                  </h2>
                  <p className="mt-1 mb-3 text-sm text-ink-500 max-w-[62ch]">
                    Gennem roller, der er ophørt, og stiftere. De to har ikke nødvendigvis været med på
                    samme tid.
                  </p>
                  <ConnectionResults result={past} past />
                </section>
              )}
              {past && past.paths.length === 0 && result.paths.length === 0 && (
                <p className="mt-2 text-sm text-ink-500">Heller ingen gennem tidligere roller og stiftere.</p>
              )}
            </>
          )}
          <p className="mt-6 text-[13px] text-ink-500 max-w-[70ch]">
            Bygger på registreringer i CVR. Revisorer indgår ikke, og udenlandske selskaber kan ikke
            følges videre. En forbindelse i CVR er ikke i sig selv et
            bevis på samarbejde.
          </p>
        </section>
      </div>
    </Container>
  )
}

export default FindConnectionPage
